import { ReactNode } from 'react';
import { IBaseItem } from '@/utils/types/Items';
import { rarityColor } from '@/utils/rarity';
import { iconBackgroundStyle } from '@/utils/iconBackground';

interface Props {
    items: IBaseItem[];
    onRemove: (id: string | number | bigint) => void;
    total: number;
    /** Top "balance" card (market). Omit on deposit (no balance card). */
    balance?: number;
    /** Title above the items list */
    sectionTitle?: string;
    /** Footer (BUY for market / DEPOSIT+CONVERT for deposit) */
    footer: ReactNode;
    /** Warning text below total */
    warning?: string;
}

// Gold coin glyph used for money figures across the new design.
const GoldCoin = ({ size = 16 }: { size?: number }) => (
    <span
        className="grid place-items-center rounded-full shrink-0"
        style={{
            width: size,
            height: size,
            background: 'linear-gradient(135deg, #ffe08a, #ffc24d)',
            boxShadow: '0 0 10px -2px rgba(255,194,77,0.85), inset 0 1px 0 rgba(255,255,255,0.5)',
        }}
    >
        <span
            className="font-black leading-none text-[#5a3d00]"
            style={{ fontSize: Math.round(size * 0.6) }}
        >
            $
        </span>
    </span>
);

const CartPanel = ({
    items,
    onRemove,
    total,
    balance,
    sectionTitle = 'In cart',
    footer,
    warning,
}: Props) => {
    return (
        <aside className="surface flex flex-col gap-4 p-[18px] xl:h-full max-xl:max-h-[600px] max-md:max-h-[500px]">
            <div className="flex items-center gap-2.5">
                <span
                    className="w-1 h-[26px] rounded-sm shrink-0"
                    style={{
                        background: 'linear-gradient(#8b6dff,#3ad6ff)',
                        boxShadow: '0 0 12px #8b6dff',
                    }}
                />
                <span className="font-chat-nick inline-flex items-center gap-2 text-text_main text-[13px] font-extrabold tracking-[0.12em] uppercase">
                    <svg
                        viewBox="0 0 24 24"
                        className="w-4 h-4 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round] opacity-85"
                    >
                        <circle cx="9" cy="20" r="1.5" />
                        <circle cx="18" cy="20" r="1.5" />
                        <path d="M3 4h2l2.6 12.5a2 2 0 0 0 2 1.5h7.5a2 2 0 0 0 2-1.6L21 7H6" />
                    </svg>
                    Cart
                </span>
            </div>

            {balance !== undefined && (
                <div className="px-4 py-3 rounded-fl-md flex items-center justify-between gap-3 bg-layer_2 border border-stroke">
                    <span className="font-chat-nick text-[10.5px] text-text_3 tracking-[0.12em] uppercase font-bold">
                        Your balance
                    </span>
                    <span className="font-chat-nick inline-flex items-center gap-1.5 text-[17px] font-extrabold text-accent_5 tabular-nums">
                        <GoldCoin />
                        {balance.toFixed(2)}
                    </span>
                </div>
            )}

            <div className="flex items-center justify-between font-chat-nick text-[10.5px] text-text_4 uppercase tracking-[0.14em] font-bold">
                <span>{sectionTitle}</span>
                <b className="text-text_2 font-extrabold tabular-nums">{items.length}</b>
            </div>

            <div className="fluent-scrollbar flex-1 min-h-0 overflow-y-auto pr-1.5 [scrollbar-gutter:stable]">
                {items.length === 0 ? (
                    <div className="text-center text-text_4 text-xs p-6 border border-dashed border-stroke rounded-fl-md flex flex-col items-center gap-2.5">
                        <svg
                            viewBox="0 0 24 24"
                            className="w-9 h-9 stroke-current fill-none [stroke-width:1.5] opacity-40"
                        >
                            <circle cx="9" cy="20" r="1.5" />
                            <circle cx="18" cy="20" r="1.5" />
                            <path d="M3 4h2l2.6 12.5a2 2 0 0 0 2 1.5h7.5a2 2 0 0 0 2-1.6L21 7H6" />
                        </svg>
                        <span className="leading-snug font-chat-msg">
                            Pick items from the grid on the left
                        </span>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 max-md:grid-cols-2 gap-2">
                        {items.map((item) => {
                            const rarity = rarityColor(item.colorHex);
                            return (
                                <div
                                    key={String(item.id)}
                                    onClick={() => onRemove(item.id)}
                                    style={
                                        {
                                            ['--rarity' as string]: rarity,
                                            background: `linear-gradient(160deg, color-mix(in srgb, ${rarity} 20%, transparent), color-mix(in srgb, ${rarity} 4%, transparent))`,
                                            borderColor: `color-mix(in srgb, ${rarity} 28%, transparent)`,
                                            animation:
                                                'cartItemPop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        } as React.CSSProperties
                                    }
                                    className="group relative border rounded-fl flex flex-col items-center pt-3 pb-2 px-1.5 cursor-pointer transition-all hover:-translate-y-0.5 hover:[border-color:var(--rarity)] hover:shadow-[inset_0_0_14px_-3px_var(--rarity),0_0_0_1.5px_var(--rarity)]"
                                >
                                    <span
                                        className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full pointer-events-none"
                                        style={{ background: rarity, boxShadow: `0 0 5px ${rarity}` }}
                                    />
                                    {item.icon ? (
                                        <img
                                            src={item.icon}
                                            alt=""
                                            draggable={false}
                                            className="w-[52px] h-[52px] object-contain"
                                            style={{
                                                filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.45))',
                                                ...iconBackgroundStyle(item.iconBackground),
                                            }}
                                        />
                                    ) : (
                                        <div className="w-[52px] h-[52px] bg-layer_3 rounded" />
                                    )}
                                    <div className="font-chat-nick text-[11px] font-extrabold text-accent_5 mt-1 tabular-nums">
                                        ${item.priceUsd.toFixed(2)}
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemove(item.id);
                                        }}
                                        aria-label="Remove"
                                        className="absolute top-1 right-1 w-[18px] h-[18px] rounded-full grid place-items-center text-text_2 text-sm bg-black/55 opacity-0 group-hover:opacity-100 hover:!bg-danger hover:text-white transition-all"
                                    >
                                        ×
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="border-t border-stroke pt-3.5 flex flex-col gap-3">
                {warning && (
                    <div className="text-[11px] text-danger text-center font-semibold -mb-1 font-chat-msg">
                        {warning}
                    </div>
                )}
                <div className="flex items-center justify-between">
                    <span className="font-chat-nick text-[10.5px] text-text_3 uppercase tracking-[0.12em] font-bold">
                        Total
                    </span>
                    <span className="font-chat-nick inline-flex items-center gap-1.5 text-[18px] font-extrabold text-accent_5 tabular-nums">
                        <GoldCoin size={17} />
                        {total.toFixed(2)}
                    </span>
                </div>
                {footer}
            </div>
        </aside>
    );
};

export default CartPanel;
